import api from '../../../app/api';
import { PaginatedList } from '../../../shared/models/paginated-list';
import { TraceLogs } from '../../../shared/models/trace-logs';
import { transformTraceLogsResponse } from '../../../shared/utils/transform-trace-logs-response';
import { ClientUser } from '../models/client-user';
import { ClientUserDetails } from '../models/client-user-details';
import { ClientUserParams } from '../models/client-user-params';
import { ClientUserRole } from '../models/client-user-role';
import { ClientUsersParams } from '../models/client-users-params';

export const clientUsersApi = api
  .enhanceEndpoints({
    addTagTypes: ['ClientUsers', 'ClientUser', 'ClientUserTraceLogs', 'CurrentUser', 'Templates', 'ClientGroups'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getClientUsers: builder.query<PaginatedList<ClientUser>, ClientUsersParams>({
        query: params => ({
          url: '/portal-api/v1/client-users',
          params,
        }),
        providesTags: ['ClientUsers'],
      }),
      getClientUser: builder.query<ClientUserDetails, string>({
        query: (id: string) => ({
          url: `/portal-api/v1/client-users/${id}`,
        }),
        providesTags: result => (result?.id ? [{ type: 'ClientUser', id: result.id }] : []),
      }),
      deleteClientUser: builder.mutation<void, string>({
        query: id => ({
          url: `/portal-api/v1/client-users/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, id) => ['ClientUsers', { type: 'ClientUser', id }],
      }),
      getClientUsersRoles: builder.query<ClientUserRole[], void>({
        query: () => ({
          url: '/portal-api/v1/client-users/roles',
        }),
      }),
      createClientUser: builder.mutation<ClientUserDetails, Omit<ClientUserParams, 'id'>>({
        query: body => ({
          url: '/portal-api/v1/client-users',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['ClientUsers', 'ClientGroups'],
      }),
      updateClientUser: builder.mutation<ClientUserDetails, ClientUserParams>({
        query: body => ({
          url: `/portal-api/v1/client-users/${body.id}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: result =>
          result?.id ? ['ClientUsers', 'ClientGroups', 'Templates', { type: 'ClientUser', id: result.id }] : [],
      }),
      getClientUserTraceLogs: builder.query<PaginatedList<TraceLogs>, { id: string; page: number }>({
        query: ({ id, ...params }) => ({
          url: `/portal-api/v1/client-users/${id}/trace-logs`,
          params,
        }),
        providesTags: ['ClientUserTraceLogs'],
        transformResponse: transformTraceLogsResponse,
      }),
      getClientUsersUnits: builder.query<string[], void>({
        query: () => ({
          url: '/portal-api/v1/client-users/units',
        }),
      }),
      resetPassword: builder.mutation<ClientUserDetails, string>({
        query: id => ({
          url: `/portal-api/v1/client-users/${id}/reset-password`,
          method: 'POST',
        }),
        invalidatesTags: ['CurrentUser'],
      }),
      resetSignature: builder.mutation<ClientUserDetails, string>({
        query: id => ({
          url: `/portal-api/v1/client-users/${id}/reset-signature`,
          method: 'PUT',
        }),
        invalidatesTags: ['CurrentUser'],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetClientUsersQuery,
  useGetClientUserQuery,
  useGetClientUsersRolesQuery,
  useCreateClientUserMutation,
  useUpdateClientUserMutation,
  useDeleteClientUserMutation,
  useGetClientUserTraceLogsQuery,
  useGetClientUsersUnitsQuery,
  useResetPasswordMutation,
  useResetSignatureMutation,
} = clientUsersApi;
